/**
 * Smart Shikka AI Core — Kami Document Renderer Preview Registry
 * PR81: No live render execution. Deep-frozen preview metadata.
 * Adapted from Kami (https://github.com/tw93/Kami), MIT license.
 */
const BLOCKED_ACTIONS = [
    { action: "render_html_live", reason: "No live HTML rendering in PR81" },
    { action: "generate_pdf", reason: "No PDF generation in PR81" },
    { action: "write_html_file", reason: "No HTML file write in PR81" },
    { action: "write_pdf_file", reason: "No PDF file write in PR81" },
    { action: "run_weasyprint", reason: "No WeasyPrint in PR81" },
    { action: "run_puppeteer", reason: "No Puppeteer in PR81" },
    { action: "run_playwright", reason: "No Playwright in PR81" },
    { action: "browser_dom_render", reason: "No browser DOM render in PR81" },
    { action: "network_call", reason: "No network calls in PR81" },
    { action: "provider_call", reason: "No provider calls in PR81" },
    { action: "db_write", reason: "No DB writes in PR81" },
    { action: "supabase_call", reason: "No Supabase calls in PR81" },
    { action: "secret_read", reason: "No secret access in PR81" },
    { action: "file_read", reason: "No file reads in PR81" },
    { action: "file_write", reason: "No file writes in PR81" },
    { action: "child_process", reason: "No child_process in PR81" },
    { action: "shell_command", reason: "No shell commands in PR81" },
    { action: "desktop_action", reason: "No desktop automation in PR81" },
    { action: "browser_action", reason: "No browser automation in PR81" },
];
function deepFreeze(obj) {
    Object.freeze(obj);
    for (const value of Object.values(obj)) {
        if (value && typeof value === "object" && !Object.isFrozen(value)) {
            deepFreeze(value);
        }
    }
    return obj;
}
const previews = [
    {
        id: "changelog",
        sourceTemplateId: "changelog",
        displayName: "Smart Shikka AI Changelog Template Preview, adapted from Kami",
        description: "Changelog document template preview — metadata only, no live render",
        supportedContentKinds: ["changelog", "release-notes"],
        requiredFields: ["title", "entries"],
        optionalFields: ["subtitle", "date", "version"],
        previewOnly: true,
        liveRenderAllowedInPr81: false,
        pdfGenerationAllowedInPr81: false,
        htmlFileWriteAllowedInPr81: false,
        browserRenderAllowedInPr81: false,
        networkAllowedInPr81: false,
        providerAllowedInPr81: false,
        blockedActions: [...BLOCKED_ACTIONS],
        futurePrScope: ["Future: live render with owner approval"],
        notes: ["Source: Kami changelog.html template"],
    },
    {
        id: "letter",
        sourceTemplateId: "letter",
        displayName: "Smart Shikka AI Letter Template Preview, adapted from Kami",
        description: "Letter document template preview — metadata only, no live render",
        supportedContentKinds: ["letter", "correspondence"],
        requiredFields: ["recipient", "body", "sender"],
        optionalFields: ["date", "subject", "signature"],
        previewOnly: true,
        liveRenderAllowedInPr81: false,
        pdfGenerationAllowedInPr81: false,
        htmlFileWriteAllowedInPr81: false,
        browserRenderAllowedInPr81: false,
        networkAllowedInPr81: false,
        providerAllowedInPr81: false,
        blockedActions: [...BLOCKED_ACTIONS],
        futurePrScope: ["Future: live render with owner approval"],
        notes: ["Source: Kami letter.html template"],
    },
    {
        id: "long-doc",
        sourceTemplateId: "long-doc",
        displayName: "Smart Shikka AI Long Document Template Preview, adapted from Kami",
        description: "Long document template preview — metadata only, no live render",
        supportedContentKinds: ["report", "documentation", "manual"],
        requiredFields: ["title", "sections"],
        optionalFields: ["toc", "author", "date", "abstract"],
        previewOnly: true,
        liveRenderAllowedInPr81: false,
        pdfGenerationAllowedInPr81: false,
        htmlFileWriteAllowedInPr81: false,
        browserRenderAllowedInPr81: false,
        networkAllowedInPr81: false,
        providerAllowedInPr81: false,
        blockedActions: [...BLOCKED_ACTIONS],
        futurePrScope: ["Future: live render with owner approval"],
        notes: ["Source: Kami long-doc.html template"],
    },
    {
        id: "one-pager",
        sourceTemplateId: "one-pager",
        displayName: "Smart Shikka AI One-Pager Template Preview, adapted from Kami",
        description: "One-pager document template preview — metadata only, no live render",
        supportedContentKinds: ["summary", "brief", "one-pager"],
        requiredFields: ["title", "content"],
        optionalFields: ["logo", "tagline", "contact"],
        previewOnly: true,
        liveRenderAllowedInPr81: false,
        pdfGenerationAllowedInPr81: false,
        htmlFileWriteAllowedInPr81: false,
        browserRenderAllowedInPr81: false,
        networkAllowedInPr81: false,
        providerAllowedInPr81: false,
        blockedActions: [...BLOCKED_ACTIONS],
        futurePrScope: ["Future: live render with owner approval"],
        notes: ["Source: Kami one-pager.html template"],
    },
    {
        id: "portfolio",
        sourceTemplateId: "portfolio",
        displayName: "Smart Shikka AI Portfolio Template Preview, adapted from Kami",
        description: "Portfolio document template preview — metadata only, no live render",
        supportedContentKinds: ["portfolio", "showcase"],
        requiredFields: ["name", "projects"],
        optionalFields: ["bio", "skills", "contact", "photo"],
        previewOnly: true,
        liveRenderAllowedInPr81: false,
        pdfGenerationAllowedInPr81: false,
        htmlFileWriteAllowedInPr81: false,
        browserRenderAllowedInPr81: false,
        networkAllowedInPr81: false,
        providerAllowedInPr81: false,
        blockedActions: [...BLOCKED_ACTIONS],
        futurePrScope: ["Future: live render with owner approval"],
        notes: ["Source: Kami portfolio.html template"],
    },
    {
        id: "resume",
        sourceTemplateId: "resume",
        displayName: "Smart Shikka AI Resume Template Preview, adapted from Kami",
        description: "Resume document template preview — metadata only, no live render",
        supportedContentKinds: ["resume", "cv"],
        requiredFields: ["name", "experience", "education"],
        optionalFields: ["skills", "contact", "summary", "certifications"],
        previewOnly: true,
        liveRenderAllowedInPr81: false,
        pdfGenerationAllowedInPr81: false,
        htmlFileWriteAllowedInPr81: false,
        browserRenderAllowedInPr81: false,
        networkAllowedInPr81: false,
        providerAllowedInPr81: false,
        blockedActions: [...BLOCKED_ACTIONS],
        futurePrScope: ["Future: live render with owner approval"],
        notes: ["Source: Kami resume.html template"],
    },
    {
        id: "slides-weasy",
        sourceTemplateId: "slides-weasy",
        displayName: "Smart Shikka AI Slides Template Preview, adapted from Kami",
        description: "Slides document template preview — metadata only, no live render",
        supportedContentKinds: ["slides", "presentation"],
        requiredFields: ["title", "slides"],
        optionalFields: ["author", "theme", "footer"],
        previewOnly: true,
        liveRenderAllowedInPr81: false,
        pdfGenerationAllowedInPr81: false,
        htmlFileWriteAllowedInPr81: false,
        browserRenderAllowedInPr81: false,
        networkAllowedInPr81: false,
        providerAllowedInPr81: false,
        blockedActions: [...BLOCKED_ACTIONS],
        futurePrScope: ["Future: live render with owner approval"],
        notes: ["Source: Kami slides-weasy.html template"],
    },
];
const registry = deepFreeze({ previews });
export function getKamiDocumentRenderPreviews() {
    return registry.previews;
}
export function getKamiDocumentRenderPreviewById(id) {
    return registry.previews.find((p) => p.id === id);
}
export { registry as kamiDocumentRendererPreviewRegistry };
//# sourceMappingURL=kamiDocumentRendererPreviewRegistry.js.map